import { useState, useEffect, useRef } from 'react';
import Modal from './Modal';
import ReviewLayout from './ReviewLayout';
import { CONDITIONS, buildCardTitle, mergeCardImages } from './titleUtils';
import Reseller from './Reseller';
import { SignIn, useAuth, useClerk, UserButton } from '@clerk/react';

const RESELLER_USER_IDS = ['user_3BCjA4weoBnajSAikWSX7UkvlC6', 'user_3CFLjclKTHYzmdho62av96S6GPs'];

function useIsMobile() {
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  useEffect(() => {
    const handler = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handler);
    return () => window.removeEventListener('resize', handler);
  }, []);
  return isMobile;
}

const API_BASE = process.env.REACT_APP_API_URL || '';

const SETTINGS_KEY = 'bodega_settings';
const DEFAULT_SETTINGS = {
  ebayMarket: 'AU',
  shippingProfile: '',
  returnProfile: '',
  paymentProfile: '',
  location: '',
  customSku: '',
  scheduleDate: '',
  cardsPerDay: '',
  storeCategoryId: '',
  descriptionMode: 'auto',
  customDescription: '',
  detectionRules: [],
  listingTemplates: [],
  hideBranding: false,
};

// UI gating only — the server (resolveHideBranding in server/index.js) is the
// authoritative copy of this list; keep both in sync when plans change
const WHITE_LABEL_PLANS = ['concord', 'promo', 'pro', 'unlimited'];

function loadSettings() {
  try { return { ...DEFAULT_SETTINGS, ...JSON.parse(localStorage.getItem(SETTINGS_KEY) || '{}') }; }
  catch { return { ...DEFAULT_SETTINGS }; }
}

function saveSettings(s) {
  localStorage.setItem(SETTINGS_KEY, JSON.stringify(s));
}

// ── Export history — stored per user in localStorage, newest first, capped ──
const EXPORT_HISTORY_KEY = 'bodega_export_history';
const EXPORT_HISTORY_MAX = 20;

export function loadExportHistory(userId) {
  try {
    const list = JSON.parse(localStorage.getItem(`${EXPORT_HISTORY_KEY}_${userId}`) || '[]');
    return Array.isArray(list) ? list : [];
  } catch { return []; }
}

export function recordExport(userId, entry) {
  let list = [{ id: Date.now(), ...entry }, ...loadExportHistory(userId)].slice(0, EXPORT_HISTORY_MAX);
  const key = `${EXPORT_HISTORY_KEY}_${userId}`;
  // On quota errors drop the oldest half and retry, so history degrades
  // instead of silently failing to record forever
  while (true) {
    try { localStorage.setItem(key, JSON.stringify(list)); break; }
    catch {
      if (list.length === 0) break;
      list = list.slice(0, Math.floor(list.length / 2));
    }
  }
  return list;
}

export function deleteExportEntry(userId, id) {
  const list = loadExportHistory(userId).filter(e => e.id !== id);
  try { localStorage.setItem(`${EXPORT_HISTORY_KEY}_${userId}`, JSON.stringify(list)); } catch {}
  return list;
}

export function SettingsModal({ theme, settings, onChange, onClose, marketingConsent, onMarketingConsentChange, plan = 'free', exportHistory = [], onRedownload, onDeleteExport }) {
  const [showHotkeys, setShowHotkeys] = useState(false);
  const [showChangelog, setShowChangelog] = useState(false);
  const [redownloading, setRedownloading] = useState(null);
  const canWhiteLabel = WHITE_LABEL_PLANS.includes(plan);
  const border = theme === 'dark' ? '#2e2c28' : '#dddad4';
  const bg     = theme === 'dark' ? '#141412' : '#f7f6f3';
  const bg2    = theme === 'dark' ? '#1c1b18' : '#ffffff';
  const text   = theme === 'dark' ? '#f0ede8' : '#1a1916';
  const text2  = theme === 'dark' ? '#857f76' : '#6b6760';

  const field = (label, key, placeholder) => (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <label style={{ fontSize: 11, fontFamily: 'IBM Plex Mono, monospace', color: text2, letterSpacing: '0.05em', textTransform: 'uppercase' }}>{label}</label>
      <input
        value={settings[key]}
        onChange={e => onChange({ ...settings, [key]: e.target.value })}
        placeholder={placeholder}
        style={{ height: 36, padding: '0 12px', borderRadius: 6, border: `1px solid ${border}`, background: bg, color: text, fontSize: 13, fontFamily: 'IBM Plex Sans, sans-serif', outline: 'none', boxSizing: 'border-box', width: '100%' }}
      />
    </div>
  );

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: bg2, border: `1px solid ${border}`, borderRadius: 10, padding: 28, width: 720, maxHeight: '88vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <div>
            <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 13, fontWeight: 600, color: text }}>Settings</div>
            <div style={{ fontSize: 12, color: text2, marginTop: 4 }}>Saved automatically. Used as defaults for every export.</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: text2, fontSize: 18, lineHeight: 1, padding: 0, marginLeft: 16 }}>✕</button>
        </div>

        <div style={{ display: 'flex', gap: 32 }}>
          {/* Left column */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', color: text2, letterSpacing: '0.05em', marginBottom: 8 }}>EBAY MARKET</div>
              <select
                value={settings.ebayMarket}
                onChange={e => onChange({ ...settings, ebayMarket: e.target.value })}
                style={{ height: 36, padding: '0 12px', borderRadius: 6, border: `1px solid ${border}`, background: bg, color: text, fontSize: 13, fontFamily: 'IBM Plex Sans, sans-serif', outline: 'none', width: '100%' }}>
                <option value="AU">Australia (AUD)</option>
                <option value="US">United States (USD)</option>
                <option value="UK">United Kingdom (GBP)</option>
                <option value="CA">Canada (CAD)</option>
                <option value="DE">Germany (EUR)</option>
              </select>
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', color: text2, letterSpacing: '0.05em', marginBottom: 8 }}>EBAY PROFILES</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {field('Shipping Profile Name', 'shippingProfile', 'e.g. [Paid by Customer - Tier 4]')}
                {field('Return Profile Name', 'returnProfile', 'e.g. Trading Cards - No Returns')}
                {field('Payment Profile Name', 'paymentProfile', 'e.g. Trading Cards - No Immediate Payment')}
                {field('Location', 'location', 'e.g. Melbourne, VIC')}
              </div>
            </div>
          </div>

          {/* Right column */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              <div style={{ fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', color: text2, letterSpacing: '0.05em', marginBottom: 8 }}>EXPORT DEFAULTS</div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                {field('Custom SKU', 'customSku', 'e.g. bboard004')}
                {field('Store Category ID', 'storeCategoryId', 'e.g. 4137376419')}
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <label style={{ fontSize: 11, fontFamily: 'IBM Plex Mono, monospace', color: text2, letterSpacing: '0.05em', textTransform: 'uppercase' }}>Scheduled Date</label>
                  <input
                    type="datetime-local"
                    value={settings.scheduleDate}
                    onChange={e => onChange({ ...settings, scheduleDate: e.target.value })}
                    style={{ height: 36, padding: '0 12px', borderRadius: 6, border: `1px solid ${border}`, background: bg, color: settings.scheduleDate ? text : text2, fontSize: 13, outline: 'none', cursor: 'pointer', boxSizing: 'border-box', width: '100%' }}
                  />
                </div>
                {settings.scheduleDate && new Date(settings.scheduleDate) > new Date(Date.now() + 21 * 86400000) && (
                  <div style={{ fontSize: 11, color: '#b94040', fontFamily: 'IBM Plex Mono, monospace' }}>eBay only allows scheduling up to 21 days in advance.</div>
                )}
                {settings.scheduleDate && field('Cards Per Day', 'cardsPerDay', 'e.g. 5')}
              </div>
            </div>
          </div>
        </div>

        {/* Description section — full width */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10, borderTop: `1px solid ${border}`, paddingTop: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', color: text2, letterSpacing: '0.05em' }}>LISTING DESCRIPTION</div>
              <div style={{ fontSize: 11, color: text2, marginTop: 3 }}>Card title and "Listed with Bodega." always appear at the top.</div>
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              {['auto', 'custom'].map(mode => (
                <button
                  key={mode}
                  onClick={() => onChange({ ...settings, descriptionMode: mode })}
                  style={{ height: 28, padding: '0 12px', borderRadius: 5, border: `1px solid ${settings.descriptionMode === mode ? text : border}`, background: settings.descriptionMode === mode ? text : 'transparent', color: settings.descriptionMode === mode ? bg : text2, fontSize: 11, fontFamily: 'IBM Plex Mono, monospace', cursor: 'pointer', letterSpacing: '0.03em' }}>
                  {mode === 'auto' ? 'Default' : 'Custom'}
                </button>
              ))}
            </div>
          </div>
          {settings.descriptionMode === 'custom' && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
              <textarea
                value={settings.customDescription}
                onChange={e => onChange({ ...settings, customDescription: e.target.value })}
                placeholder="Write your listing description here. Plain text or HTML — just like eBay's description editor."
                rows={6}
                style={{ padding: '10px 12px', borderRadius: 6, border: `1px solid ${border}`, background: bg, color: text, fontSize: 13, fontFamily: 'IBM Plex Sans, sans-serif', outline: 'none', resize: 'vertical', width: '100%', boxSizing: 'border-box', lineHeight: 1.5 }}
              />
              <div style={{ fontSize: 11, color: text2 }}>Tip: you can use HTML tags like &lt;b&gt;, &lt;p&gt;, &lt;br&gt; — eBay renders them in the listing.</div>
            </div>
          )}
          {settings.descriptionMode === 'auto' && (
            <div style={{ fontSize: 11, color: text2, fontStyle: 'italic' }}>Switch to Custom to add your own returns, shipping, and condition details.</div>
          )}
          <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: canWhiteLabel ? 'pointer' : 'default', opacity: canWhiteLabel ? 1 : 0.55, marginTop: 4 }}>
            <input
              type="checkbox"
              disabled={!canWhiteLabel}
              checked={!!settings.hideBranding}
              onChange={e => onChange({ ...settings, hideBranding: e.target.checked })}
              style={{ accentColor: '#4a9b6f', width: 15, height: 15, flexShrink: 0 }}
            />
            <span style={{ fontSize: 12, color: text2 }}>
              Remove "Listed with Bodega." from descriptions
              {!canWhiteLabel && <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#c47c2b', marginLeft: 8 }}>PRO / UNLIMITED</span>}
            </span>
          </label>
        </div>

        {/* Listing Templates */}
        <div style={{ borderTop: `1px solid ${border}`, paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', color: text2, letterSpacing: '0.05em' }}>LISTING TEMPLATES</div>
              <div style={{ fontSize: 11, color: text2, marginTop: 3 }}>Reusable presets — apply condition, price, sport and league to a card in one click during review.</div>
            </div>
            <button
              onClick={() => onChange({ ...settings, listingTemplates: [...(settings.listingTemplates || []), { name: '', condition: '', sellPrice: '', sport: '', league: '' }] })}
              style={{ height: 28, padding: '0 12px', borderRadius: 5, border: `1px solid ${border}`, background: 'transparent', color: text2, fontSize: 11, fontFamily: 'IBM Plex Mono, monospace', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              + Add template
            </button>
          </div>
          {(settings.listingTemplates || []).length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(settings.listingTemplates || []).map((tpl, i) => {
                const update = (patch) => {
                  const templates = [...settings.listingTemplates];
                  templates[i] = { ...templates[i], ...patch };
                  onChange({ ...settings, listingTemplates: templates });
                };
                return (
                  <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <input
                      value={tpl.name}
                      onChange={e => update({ name: e.target.value })}
                      placeholder="Template name, e.g. 90s Basketball"
                      style={{ flex: 1.4, height: 32, padding: '0 10px', borderRadius: 5, border: `1px solid ${border}`, background: bg, color: text, fontSize: 12, fontFamily: 'IBM Plex Sans, sans-serif', outline: 'none', minWidth: 0 }}
                    />
                    <select
                      value={tpl.condition}
                      onChange={e => update({ condition: e.target.value })}
                      style={{ width: 130, height: 32, padding: '0 8px', borderRadius: 5, border: `1px solid ${border}`, background: bg, color: tpl.condition ? text : text2, fontSize: 11, fontFamily: 'IBM Plex Mono, monospace', flexShrink: 0 }}>
                      <option value="">condition —</option>
                      {CONDITIONS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                    </select>
                    <input
                      value={tpl.sellPrice}
                      onChange={e => update({ sellPrice: e.target.value.replace(/[^0-9.]/g, '') })}
                      placeholder="$"
                      style={{ width: 60, height: 32, padding: '0 8px', borderRadius: 5, border: `1px solid ${border}`, background: bg, color: text, fontSize: 12, fontFamily: 'IBM Plex Mono, monospace', outline: 'none', flexShrink: 0 }}
                    />
                    <input
                      value={tpl.sport}
                      onChange={e => update({ sport: e.target.value })}
                      placeholder="sport"
                      style={{ width: 84, height: 32, padding: '0 8px', borderRadius: 5, border: `1px solid ${border}`, background: bg, color: text, fontSize: 12, fontFamily: 'IBM Plex Sans, sans-serif', outline: 'none', flexShrink: 0 }}
                    />
                    <input
                      value={tpl.league}
                      onChange={e => update({ league: e.target.value })}
                      placeholder="league"
                      style={{ width: 64, height: 32, padding: '0 8px', borderRadius: 5, border: `1px solid ${border}`, background: bg, color: text, fontSize: 12, fontFamily: 'IBM Plex Sans, sans-serif', outline: 'none', flexShrink: 0 }}
                    />
                    <button
                      onClick={() => { const templates = settings.listingTemplates.filter((_, j) => j !== i); onChange({ ...settings, listingTemplates: templates }); }}
                      style={{ width: 28, height: 28, borderRadius: 5, border: `1px solid ${border}`, background: 'transparent', color: text2, fontSize: 14, cursor: 'pointer', lineHeight: 1, flexShrink: 0 }}>×</button>
                  </div>
                );
              })}
            </div>
          )}
        </div>

        {/* Detection Rules */}
        <div style={{ borderTop: `1px solid ${border}`, paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div>
              <div style={{ fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', color: text2, letterSpacing: '0.05em' }}>DETECTION RULES</div>
              <div style={{ fontSize: 11, color: text2, marginTop: 3 }}>Auto-correct fields after detection. e.g. replace "NBAHOOPS" with "NBA Hoops".</div>
            </div>
            <button
              onClick={() => onChange({ ...settings, detectionRules: [...(settings.detectionRules || []), { field: 'set', match: '', replace: '' }] })}
              style={{ height: 28, padding: '0 12px', borderRadius: 5, border: `1px solid ${border}`, background: 'transparent', color: text2, fontSize: 11, fontFamily: 'IBM Plex Mono, monospace', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              + Add rule
            </button>
          </div>
          {(settings.detectionRules || []).length > 0 && (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {(settings.detectionRules || []).map((rule, i) => (
                <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                  <select
                    value={rule.field}
                    onChange={e => { const rules = [...settings.detectionRules]; rules[i] = { ...rules[i], field: e.target.value }; onChange({ ...settings, detectionRules: rules }); }}
                    style={{ width: 120, height: 32, padding: '0 8px', borderRadius: 5, border: `1px solid ${border}`, background: bg, color: text, fontSize: 12, fontFamily: 'IBM Plex Mono, monospace' }}>
                    {['player', 'set', 'subset', 'team', 'features', 'manufacturer', 'sport', 'league'].map(f => <option key={f} value={f}>{f}</option>)}
                  </select>
                  <input
                    value={rule.match}
                    onChange={e => { const rules = [...settings.detectionRules]; rules[i] = { ...rules[i], match: e.target.value }; onChange({ ...settings, detectionRules: rules }); }}
                    placeholder="contains..."
                    style={{ flex: 1, height: 32, padding: '0 10px', borderRadius: 5, border: `1px solid ${border}`, background: bg, color: text, fontSize: 12, fontFamily: 'IBM Plex Sans, sans-serif', outline: 'none' }}
                  />
                  <span style={{ fontSize: 11, color: text2, flexShrink: 0 }}>→</span>
                  <input
                    value={rule.replace}
                    onChange={e => { const rules = [...settings.detectionRules]; rules[i] = { ...rules[i], replace: e.target.value }; onChange({ ...settings, detectionRules: rules }); }}
                    placeholder="replace with..."
                    style={{ flex: 1, height: 32, padding: '0 10px', borderRadius: 5, border: `1px solid ${border}`, background: bg, color: text, fontSize: 12, fontFamily: 'IBM Plex Sans, sans-serif', outline: 'none' }}
                  />
                  <button
                    onClick={() => { const rules = settings.detectionRules.filter((_, j) => j !== i); onChange({ ...settings, detectionRules: rules }); }}
                    style={{ width: 28, height: 28, borderRadius: 5, border: `1px solid ${border}`, background: 'transparent', color: text2, fontSize: 14, cursor: 'pointer', lineHeight: 1, flexShrink: 0 }}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Export History */}
        <div style={{ borderTop: `1px solid ${border}`, paddingTop: 16, display: 'flex', flexDirection: 'column', gap: 10 }}>
          <div>
            <div style={{ fontSize: 10, fontFamily: 'IBM Plex Mono, monospace', color: text2, letterSpacing: '0.05em' }}>EXPORT HISTORY</div>
            <div style={{ fontSize: 11, color: text2, marginTop: 3 }}>Your last {EXPORT_HISTORY_MAX} exports on this device — re-download any CSV.</div>
          </div>
          {exportHistory.length === 0 ? (
            <div style={{ fontSize: 11, color: text2, fontStyle: 'italic' }}>No exports yet.</div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 180, overflowY: 'auto' }}>
              {exportHistory.map(entry => (
                <div key={entry.id} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '7px 10px', background: bg, border: `1px solid ${border}`, borderRadius: 5 }}>
                  <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: text, whiteSpace: 'nowrap' }}>
                    {new Date(entry.date).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                  </span>
                  <span style={{ fontSize: 11, color: text2, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {entry.count} card{entry.count !== 1 ? 's' : ''}{entry.sku ? ` · ${entry.sku}` : ''}{entry.type === 'email' ? ' · emailed' : ''}
                  </span>
                  <button
                    disabled={redownloading === entry.id}
                    onClick={async () => {
                      setRedownloading(entry.id);
                      try { await onRedownload(entry); } finally { setRedownloading(null); }
                    }}
                    style={{ height: 24, padding: '0 10px', borderRadius: 4, border: `1px solid ${border}`, background: 'transparent', color: '#4a9b6f', fontSize: 11, fontFamily: 'IBM Plex Mono, monospace', cursor: 'pointer', whiteSpace: 'nowrap', flexShrink: 0 }}>
                    {redownloading === entry.id ? '...' : '↓ CSV'}
                  </button>
                  <button
                    onClick={() => onDeleteExport(entry.id)}
                    style={{ width: 24, height: 24, borderRadius: 4, border: 'none', background: 'transparent', color: text2, fontSize: 13, cursor: 'pointer', lineHeight: 1, flexShrink: 0 }}>×</button>
                </div>
              ))}
            </div>
          )}
        </div>

        {marketingConsent !== undefined && (
          <div style={{ borderTop: `1px solid ${border}`, paddingTop: 16 }}>
            <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!marketingConsent}
                onChange={e => onMarketingConsentChange(e.target.checked)}
                style={{ accentColor: '#4a9b6f', width: 15, height: 15, flexShrink: 0 }}
              />
              <span style={{ fontSize: 12, color: text2 }}>Send me occasional product updates and tips</span>
            </label>
          </div>
        )}

        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            <button
              onClick={() => setShowHotkeys(true)}
              style={{ height: 34, padding: '0 14px', borderRadius: 6, border: `1px solid ${border}`, background: 'transparent', color: text2, fontSize: 12, cursor: 'pointer', fontFamily: 'IBM Plex Mono, monospace' }}>
              ⌨ Hotkeys
            </button>
            <button
              onClick={() => setShowChangelog(true)}
              style={{ height: 34, padding: '0 14px', borderRadius: 6, border: 'none', background: 'transparent', color: text2, fontSize: 11, cursor: 'pointer', fontFamily: 'IBM Plex Mono, monospace', textDecoration: 'underline' }}>
              Changelog
            </button>
          </div>
          <button onClick={onClose} style={{ height: 34, padding: '0 20px', borderRadius: 6, border: 'none', background: text, color: bg, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>Done</button>
        </div>

        {showHotkeys && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
            <div style={{ background: bg2, border: `1px solid ${border}`, borderRadius: 10, padding: 28, width: 380, display: 'flex', flexDirection: 'column', gap: 20 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 13, fontWeight: 600, color: text }}>Hotkeys</div>
                <button onClick={() => setShowHotkeys(false)} style={{ background: 'transparent', border: 'none', color: text2, fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>
              </div>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                {[
                  ['Enter', 'Approve card / next card'],
                  ['←  →', 'Previous / next card'],
                  ['1 – 9', 'Apply database suggestion'],
                  ['Tab', 'Move between fields'],
                  ['Shift + ←', 'Flip to card front'],
                  ['Shift + →', 'Flip to card back'],
                  ['Shift + Z', 'Toggle image zoom'],
                  ['Shift + =', 'Increase quantity'],
                  ['Shift + −', 'Decrease quantity'],
                ].map(([key, label]) => (
                  <div key={key} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: `1px solid ${border}` }}>
                    <span style={{ fontSize: 12, color: text2 }}>{label}</span>
                    <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 11, color: text, background: bg, border: `1px solid ${border}`, borderRadius: 4, padding: '3px 8px' }}>{key}</span>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
                <button onClick={() => setShowHotkeys(false)} style={{ height: 34, padding: '0 20px', borderRadius: 6, border: 'none', background: text, color: bg, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>Close</button>
              </div>
            </div>
          </div>
        )}

        {showChangelog && (
          <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
            <div style={{ background: bg2, border: `1px solid ${border}`, borderRadius: 10, padding: 28, width: 480, maxHeight: '70vh', display: 'flex', flexDirection: 'column', gap: 16 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 13, fontWeight: 600, color: text }}>Changelog</div>
                <button onClick={() => setShowChangelog(false)} style={{ background: 'transparent', border: 'none', color: text2, fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>
              </div>
              <div style={{ overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 20 }}>
                {[
                  { version: '1.5.0', date: '2026-04-19', changes: ['Listing description redesigned — cleaner black bar with Bodega. branding', 'Duplicate card dedup on export — same photos won\'t appear twice in CSV'] },
                  { version: '1.4.0', date: '2026-04-18', changes: ['Session resume — pick up where you left off after closing the browser', 'Edits, quantity, and detection results saved in real-time', 'Removed "AI" label from detected cards'] },
                  { version: '1.3.0', date: '2026-04-17', changes: ['Settings saved to DB — syncs across devices and browsers'] },
                  { version: '1.2.0', date: '2026-04-17', changes: ['Quantity per card — set duplicates, exports with correct quantity', 'Hotkeys for quantity (Shift+= / Shift+−) and zoom (Shift+Z)'] },
                  { version: '1.1.0', date: '2026-04', changes: ['Editable listing description in Settings', '80-character limit on manual title overrides', 'Sell price currency label reflects selected eBay market'] },
                  { version: '1.0.0', date: '2026-04-12', changes: ['Public launch — card detection, eBay CSV export, Clerk auth, Stripe billing, multi-market support'] },
                ].map(({ version, date, changes }) => (
                  <div key={version}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'baseline', marginBottom: 8 }}>
                      <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, fontWeight: 600, color: text }}>v{version}</span>
                      <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: text2 }}>{date}</span>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      {changes.map(c => (
                        <div key={c} style={{ fontSize: 12, color: text2, display: 'flex', gap: 8 }}>
                          <span style={{ color: text2, flexShrink: 0 }}>—</span> {c}
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', borderTop: `1px solid ${border}`, paddingTop: 16 }}>
                <button onClick={() => setShowChangelog(false)} style={{ height: 34, padding: '0 20px', borderRadius: 6, border: 'none', background: text, color: bg, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>Close</button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function FeedbackModal({ theme, getToken, onClose }) {
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState('idle'); // idle | sending | sent | error
  const border = theme === 'dark' ? '#2e2c28' : '#dddad4';
  const bg     = theme === 'dark' ? '#141412' : '#f7f6f3';
  const bg2    = theme === 'dark' ? '#1c1b18' : '#ffffff';
  const text   = theme === 'dark' ? '#f0ede8' : '#1a1916';
  const text2  = theme === 'dark' ? '#857f76' : '#6b6760';

  const submit = async () => {
    if (!message.trim() || status === 'sending') return;
    setStatus('sending');
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/api/feedback`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ message: message.trim() }),
      });
      if (!r.ok) throw new Error('request failed');
      setStatus('sent');
    } catch {
      setStatus('error');
    }
  };

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: bg2, border: `1px solid ${border}`, borderRadius: 10, padding: 28, width: 440, display: 'flex', flexDirection: 'column', gap: 16 }}>
        {status === 'sent' ? (
          <>
            <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 13, fontWeight: 600, color: text }}>Thanks!</div>
            <div style={{ fontSize: 12, color: text2 }}>Got it — really appreciate you taking the time.</div>
            <button onClick={onClose} style={{ alignSelf: 'flex-start', height: 34, padding: '0 16px', borderRadius: 5, border: 'none', background: text, color: bg, fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>Close</button>
          </>
        ) : (
          <>
            <div>
              <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 13, fontWeight: 600, color: text }}>Feedback</div>
              <div style={{ fontSize: 12, color: text2, marginTop: 4 }}>Bug, missing feature, or just an idea — goes straight to the person building this.</div>
            </div>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="What's on your mind?"
              rows={5}
              autoFocus
              style={{ padding: 12, borderRadius: 6, border: `1px solid ${border}`, background: bg, color: text, fontSize: 13, fontFamily: 'IBM Plex Sans, sans-serif', outline: 'none', resize: 'vertical' }}
            />
            {status === 'error' && <div style={{ fontSize: 12, color: '#e74c3c' }}>Couldn't send that — try again?</div>}
            <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
              <button onClick={onClose} style={{ height: 34, padding: '0 16px', borderRadius: 5, border: `1px solid ${border}`, background: 'transparent', color: text2, fontSize: 12, cursor: 'pointer' }}>Cancel</button>
              <button
                onClick={submit}
                disabled={!message.trim() || status === 'sending'}
                style={{ height: 34, padding: '0 16px', borderRadius: 5, border: 'none', background: text, color: bg, fontSize: 12, fontWeight: 500, cursor: message.trim() ? 'pointer' : 'default', opacity: message.trim() ? 1 : 0.5 }}
              >
                {status === 'sending' ? 'Sending...' : 'Send'}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function ExportModal({ theme, approvedCards, onConfirm, onEmailConfirm, userEmail, onClose, limitReached, userUsage, settings, onUpgrade, onUpdateTitle, onUpdateQuantity }) {
  const [customSku, setCustomSku] = useState(settings?.customSku || '');
  const [scheduleDate, setScheduleDate] = useState(settings?.scheduleDate || '');
  const [cardsPerDay, setCardsPerDay] = useState(settings?.cardsPerDay || '');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [showTitles, setShowTitles] = useState(false);

  const border = theme === 'dark' ? '#2e2c28' : '#dddad4';
  const bg     = theme === 'dark' ? '#141412' : '#f7f6f3';
  const bg2    = theme === 'dark' ? '#1c1b18' : '#ffffff';
  const text   = theme === 'dark' ? '#f0ede8' : '#1a1916';
  const text2  = theme === 'dark' ? '#857f76' : '#6b6760';
  const text3  = theme === 'dark' ? '#4a4640' : '#a8a49e';
  const green  = '#4a9b6f';

  const limit21 = new Date(Date.now() + 21 * 86400000);
  const startDateOver = scheduleDate && new Date(scheduleDate) > limit21;
  const lastListingDate = scheduleDate && cardsPerDay && parseInt(cardsPerDay) > 0
    ? (() => { const d = new Date(scheduleDate); d.setDate(d.getDate() + Math.ceil(approvedCards.length / parseInt(cardsPerDay)) - 1); return d; })()
    : scheduleDate ? new Date(scheduleDate) : null;
  const lastListingOver = lastListingDate && lastListingDate > limit21;
  const exportBlocked = startDateOver || lastListingOver;

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 20 }}>
      <div style={{ background: bg2, border: `1px solid ${border}`, borderRadius: 10, padding: 28, width: showTitles ? 740 : 380, maxHeight: '88vh', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: 20, transition: 'width 0.2s' }}>

        {/* Header */}
        <div>
          <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 13, fontWeight: 600, color: text }}>Export CSV</div>
          <div style={{ fontSize: 12, color: text2, marginTop: 4 }}>Review your export settings before downloading.</div>
        </div>

        {/* Limit reached banner */}
        {limitReached && (
          <div style={{ background: theme === 'dark' ? '#2a1a0e' : '#fdf3e7', border: `1px solid #c47c2b`, borderRadius: 6, padding: '10px 14px' }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: '#c47c2b', marginBottom: 4 }}>Free plan limit reached</div>
            <div style={{ fontSize: 12, color: text2, lineHeight: 1.5, marginBottom: 8 }}>
              You've used all {userUsage?.limit} free cards. Export your approved cards now, then upgrade to continue listing.
            </div>
            {onUpgrade && (
              <button onClick={onUpgrade} style={{ height: 28, padding: '0 12px', borderRadius: 5, border: 'none', background: '#c47c2b', color: '#fff', fontSize: 12, cursor: 'pointer' }}>
                View upgrade options →
              </button>
            )}
          </div>
        )}

        {/* Card count */}
        <div style={{ background: bg, border: `1px solid ${border}`, borderRadius: 6, padding: '10px 14px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ fontSize: 12, color: text2 }}>Cards to export</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <button
              onClick={() => setShowTitles(s => !s)}
              style={{ height: 26, padding: '0 10px', borderRadius: 5, border: `1px solid ${showTitles ? text : border}`, background: showTitles ? text : 'transparent', color: showTitles ? bg2 : text2, fontSize: 11, fontFamily: 'IBM Plex Mono, monospace', cursor: 'pointer', whiteSpace: 'nowrap' }}>
              ✎ {showTitles ? 'Hide titles' : 'Edit titles'}
            </button>
            <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 13, fontWeight: 600, color: green }}>{approvedCards.length}</div>
          </div>
        </div>

        {/* Bulk title editor */}
        {showTitles && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 300, overflowY: 'auto', paddingRight: 2 }}>
            {approvedCards.map((card, i) => {
              const auto = buildCardTitle(card);
              const value = card.titleOverride != null ? card.titleOverride : auto;
              const over = value.length > 80;
              const edited = card.titleOverride != null;
              const qty = card.quantity || 1;
              return (
                <div key={i} style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: text3, width: 18, textAlign: 'right', flexShrink: 0 }}>{i + 1}</span>
                  <input
                    value={value}
                    onChange={e => onUpdateTitle(i, e.target.value)}
                    style={{ flex: 1, height: 32, padding: '0 10px', borderRadius: 5, border: `1px solid ${over ? '#b94040' : edited ? '#c47c2b' : border}`, background: bg, color: text, fontSize: 12, fontFamily: 'IBM Plex Sans, sans-serif', outline: 'none', minWidth: 0 }}
                  />
                  <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: over ? '#b94040' : text3, width: 40, flexShrink: 0 }}>{value.length}/80</span>
                  <button
                    onClick={() => onUpdateTitle(i, null)}
                    disabled={!edited}
                    title="Reset to auto title"
                    style={{ width: 24, height: 24, borderRadius: 4, border: 'none', background: 'transparent', color: edited ? '#c47c2b' : text3, fontSize: 12, cursor: edited ? 'pointer' : 'default', lineHeight: 1, flexShrink: 0 }}>↺</button>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 0, flexShrink: 0 }}>
                    <button
                      onClick={() => onUpdateQuantity(i, qty - 1)}
                      disabled={qty <= 1}
                      title="Decrease quantity"
                      style={{ width: 20, height: 24, background: bg, border: `1px solid ${border}`, borderRight: 'none', borderRadius: '4px 0 0 4px', fontSize: 12, color: text2, cursor: qty <= 1 ? 'default' : 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>−</button>
                    <input
                      type="number"
                      min="1"
                      max="999"
                      value={qty}
                      onChange={e => onUpdateQuantity(i, e.target.value)}
                      title="Quantity"
                      style={{ width: 30, height: 24, textAlign: 'center', fontSize: 11, color: text, background: bg, border: `1px solid ${border}`, outline: 'none', fontFamily: 'IBM Plex Mono, monospace', boxSizing: 'border-box' }}
                    />
                    <button
                      onClick={() => onUpdateQuantity(i, qty + 1)}
                      title="Increase quantity"
                      style={{ width: 20, height: 24, background: bg, border: `1px solid ${border}`, borderLeft: 'none', borderRadius: '0 4px 4px 0', fontSize: 12, color: text2, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>+</button>
                  </div>
                </div>
              );
            })}
            <div style={{ fontSize: 10, color: text3, marginTop: 2 }}>Titles over 80 characters are trimmed at the last whole word on export. Quantity changes here apply immediately.</div>
          </div>
        )}

        {/* Custom SKU */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 11, fontFamily: 'IBM Plex Mono, monospace', color: text2, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Custom SKU
          </label>
          <input
            value={customSku}
            onChange={e => setCustomSku(e.target.value)}
            placeholder="e.g. bboard004"
            style={{ height: 36, padding: '0 12px', borderRadius: 6, border: `1px solid ${border}`, background: bg, color: text, fontSize: 13, fontFamily: 'IBM Plex Mono, monospace', outline: 'none' }}
          />
          <div style={{ fontSize: 11, color: text3 }}>
            All cards in this export will use: <span style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{customSku || '—'}</span>
          </div>
        </div>

        {/* Scheduled date */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          <label style={{ fontSize: 11, fontFamily: 'IBM Plex Mono, monospace', color: text2, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
            Scheduled Date
          </label>
          <input
            type="datetime-local"
            value={scheduleDate}
            onChange={e => setScheduleDate(e.target.value)}
            max={(() => { const d = new Date(); d.setDate(d.getDate() + 21); return d.toISOString().slice(0, 16); })()}
            style={{ height: 36, padding: '0 12px', borderRadius: 6, border: `1px solid ${scheduleDate && new Date(scheduleDate) > new Date(Date.now() + 21 * 86400000) ? '#b94040' : border}`, background: bg, color: scheduleDate ? text : text2, fontSize: 13, outline: 'none', cursor: 'pointer' }}
          />
          {startDateOver ? (
            <div style={{ fontSize: 11, color: '#b94040' }}>eBay only allows scheduling up to 21 days in advance.</div>
          ) : (
            <div style={{ fontSize: 11, color: text3 }}>
              {scheduleDate ? `Listings will go live from ${new Date(scheduleDate).toLocaleString('en-AU')}` : 'Leave blank to list immediately'}
            </div>
          )}
        </div>

        {/* Cards per day */}
        {scheduleDate && (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            <label style={{ fontSize: 11, fontFamily: 'IBM Plex Mono, monospace', color: text2, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
              Cards Per Day
            </label>
            <input
              type="number"
              min="1"
              value={cardsPerDay}
              onChange={e => setCardsPerDay(e.target.value)}
              placeholder="e.g. 5"
              style={{ height: 36, padding: '0 12px', borderRadius: 6, border: `1px solid ${border}`, background: bg, color: text, fontSize: 13, outline: 'none' }}
            />
            {cardsPerDay && scheduleDate && lastListingOver ? (
              <div style={{ fontSize: 11, color: '#b94040' }}>
                {`Last batch falls on ${lastListingDate.toLocaleDateString('en-AU')} — over the 21-day limit. Reduce cards per day or start date.`}
              </div>
            ) : (
              <div style={{ fontSize: 11, color: text3 }}>
                {cardsPerDay && scheduleDate
                  ? (() => {
                      const n = parseInt(cardsPerDay);
                      const total = approvedCards.length;
                      const days = Math.ceil(total / n);
                      return `${total} cards across ${days} day${days !== 1 ? 's' : ''} — last batch on ${lastListingDate.toLocaleDateString('en-AU')}`;
                    })()
                  : 'Leave blank to schedule all cards on the same day'}
              </div>
            )}
          </div>
        )}

        {/* Buttons */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button
            onClick={onClose}
            style={{ height: 34, padding: '0 16px', borderRadius: 6, border: `1px solid ${border}`, background: 'transparent', color: text2, fontSize: 13, cursor: 'pointer' }}>
            Cancel
          </button>
          <button
            disabled={emailSending || emailSent}
            onClick={async () => {
              setEmailSending(true);
              const ok = await onEmailConfirm(customSku, scheduleDate, cardsPerDay);
              setEmailSending(false);
              if (ok) setEmailSent(true);
            }}
            style={{ height: 34, padding: '0 16px', borderRadius: 6, border: `1px solid ${border}`, background: emailSent ? '#4a9b6f' : 'transparent', color: emailSent ? '#fff' : text2, fontSize: 13, cursor: emailSending ? 'default' : 'pointer', whiteSpace: 'nowrap' }}>
            {emailSent ? '✓ Sent' : emailSending ? 'Sending...' : '✉ Email CSV'}
          </button>
          <button
            onClick={() => onConfirm(customSku, scheduleDate, cardsPerDay)}
            disabled={exportBlocked}
            style={{ height: 34, padding: '0 16px', borderRadius: 6, border: 'none', background: exportBlocked ? '#555' : text, color: bg, fontSize: 13, fontWeight: 500, cursor: exportBlocked ? 'not-allowed' : 'pointer' }}>
            ↓ Download CSV
          </button>
        </div>

      </div>
    </div>
  );
}


function OnboardingModal({ theme, onConfirm }) {
  const [optIn, setOptIn] = useState(false);
  const border = theme === 'dark' ? '#2e2c28' : '#dddad4';
  const bg2    = theme === 'dark' ? '#1c1b18' : '#ffffff';
  const text   = theme === 'dark' ? '#f0ede8' : '#1a1916';
  const text2  = theme === 'dark' ? '#857f76' : '#6b6760';

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 200 }}>
      <div style={{ background: bg2, border: `1px solid ${border}`, borderRadius: 10, padding: 32, width: 420, display: 'flex', flexDirection: 'column', gap: 20 }}>
        <div>
          <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 15, fontWeight: 700, color: text, marginBottom: 8 }}>Welcome to Bodega.</div>
          <div style={{ fontSize: 13, color: text2, lineHeight: 1.6 }}>
            List your sports cards on eBay in minutes — drop photos, review detections, export a bulk CSV.
          </div>
        </div>
        <label style={{ display: 'flex', alignItems: 'flex-start', gap: 10, cursor: 'pointer' }}>
          <input
            type="checkbox"
            checked={optIn}
            onChange={e => setOptIn(e.target.checked)}
            style={{ marginTop: 2, accentColor: '#4a9b6f', width: 15, height: 15, flexShrink: 0 }}
          />
          <span style={{ fontSize: 13, color: text2, lineHeight: 1.5 }}>
            Send me occasional product updates and tips. No spam — unsubscribe any time.
          </span>
        </label>
        <button
          onClick={() => onConfirm(optIn)}
          style={{ height: 40, borderRadius: 6, border: 'none', background: text, color: bg2, fontSize: 13, fontWeight: 600, cursor: 'pointer', fontFamily: 'IBM Plex Sans, sans-serif' }}>
          Get started
        </button>
      </div>
    </div>
  );
}

function PricingModal({ theme, currentPlan, onClose, onSelectPlan, onManageBilling, userId }) {
  const border = theme === 'dark' ? '#2e2c28' : '#dddad4';
  const bg     = theme === 'dark' ? '#141412' : '#f7f6f3';
  const bg2    = theme === 'dark' ? '#1c1b18' : '#ffffff';
  const text   = theme === 'dark' ? '#f0ede8' : '#1a1916';
  const text2  = theme === 'dark' ? '#857f76' : '#6b6760';
  const green  = '#4a9b6f';
  const [loading, setLoading] = useState(null);

  const plans = [
    {
      key: 'concord',
      name: 'Concord',
      price: '$9.95',
      limit: 'Unlimited cards',
      description: 'Everything you need to list cards on eBay — no limits.',
      features: ['Unlimited cards per month', 'eBay CSV export', 'Card detection', 'Session resume', 'Detection rules', 'Listing templates', 'Remove Bodega. branding'],
    },
  ];

  const handleSelect = async (planKey) => {
    setLoading(planKey);
    await onSelectPlan(planKey);
    setLoading(null);
  };

  const isPaid = ['concord', 'starter', 'pro', 'unlimited'].includes(currentPlan);

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}>
      <div style={{ background: bg2, border: `1px solid ${border}`, borderRadius: 12, padding: 32, width: '100%', maxWidth: 720, display: 'flex', flexDirection: 'column', gap: 24 }}>

        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div>
            <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 14, fontWeight: 600, color: text }}>Upgrade your plan</div>
            <div style={{ fontSize: 12, color: text2, marginTop: 4 }}>Billed monthly. Cancel anytime.</div>
          </div>
          <button onClick={onClose} style={{ background: 'transparent', border: 'none', color: text2, fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>

        {/* Plan cards */}
        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 16 }}>
          {plans.map(plan => {
            const isCurrent = currentPlan === plan.key;
            return (
              <div key={plan.key} style={{
                border: `1px solid ${isCurrent ? green : border}`,
                borderRadius: 8, padding: 20, display: 'flex', flexDirection: 'column', gap: 12,
                background: isCurrent ? (theme === 'dark' ? 'rgba(74,155,111,0.08)' : 'rgba(74,155,111,0.05)') : bg,
              }}>
                <div>
                  <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, fontWeight: 600, color: isCurrent ? green : text }}>{plan.name}</div>
                  <div style={{ fontSize: 22, fontWeight: 700, color: text, marginTop: 6 }}>{plan.price}<span style={{ fontSize: 12, fontWeight: 400, color: text2 }}>/mo</span></div>
                  <div style={{ fontSize: 11, color: green, marginTop: 2, fontFamily: 'IBM Plex Mono, monospace' }}>{plan.limit}</div>
                </div>
                <div style={{ fontSize: 12, color: text2, lineHeight: 1.5 }}>{plan.description}</div>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                  {plan.features.map(f => (
                    <div key={f} style={{ fontSize: 11, color: text2, display: 'flex', gap: 6, alignItems: 'center' }}>
                      <span style={{ color: green }}>✓</span> {f}
                    </div>
                  ))}
                </div>
                <button
                  onClick={() => !isCurrent && handleSelect(plan.key)}
                  disabled={isCurrent || loading === plan.key}
                  style={{
                    height: 34, borderRadius: 6, border: isCurrent ? `1px solid ${green}` : 'none',
                    background: isCurrent ? 'transparent' : text,
                    color: isCurrent ? green : bg,
                    fontSize: 12, fontWeight: 500, cursor: isCurrent ? 'default' : 'pointer',
                    opacity: loading && loading !== plan.key ? 0.5 : 1,
                  }}>
                  {isCurrent ? 'Current plan' : loading === plan.key ? 'Loading…' : 'Select plan'}
                </button>
              </div>
            );
          })}
        </div>

        {/* Manage billing */}
        {isPaid && (
          <div style={{ borderTop: `1px solid ${border}`, paddingTop: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ fontSize: 12, color: text2 }}>Need to change or cancel your subscription?</div>
            <button onClick={onManageBilling} style={{ height: 30, padding: '0 14px', borderRadius: 5, border: `1px solid ${border}`, background: 'transparent', color: text2, fontSize: 12, cursor: 'pointer' }}>
              Manage billing →
            </button>
          </div>
        )}
      </div>
    </div>
  );
}

function App() {
  const [theme, setTheme] = useState('light');
  const [showModal, setShowModal] = useState(false);
  const [showExportModal, setShowExportModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showNewBoardConfirm, setShowNewBoardConfirm] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showFeedback, setShowFeedback] = useState(false);
  const [showPricing, setShowPricing] = useState(false);

  // Show success banner if returning from Stripe
  const [upgradeSuccess, setUpgradeSuccess] = useState(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('upgrade') === 'success') {
      window.history.replaceState({}, '', '/');
      return true;
    }
    return false;
  });
  const [settings, setSettings] = useState(loadSettings);
  const [limitReached, setLimitReached] = useState(false);
  const [urls, setUrls] = useState([]);
  const [started, setStarted] = useState(false);
  const [approvedCards, setApprovedCards] = useState([]);
  const [exportedCount, setExportedCount] = useState(null);
  const [errorBanner, setErrorBanner] = useState(null);
  const [pendingSession, setPendingSession] = useState(null);
  const [showLandingPricing, setShowLandingPricing] = useState(false);
  const [resumedCards, setResumedCards] = useState([]);
  const [resumedCurrentResult, setResumedCurrentResult] = useState(null);
  const [showAdmin, setShowAdmin] = useState(false);
  const [showReseller, setShowReseller] = useState(false);
  const [adminStats, setAdminStats] = useState(null);
  const [adminLoading, setAdminLoading] = useState(false);
  const [adminUserCards, setAdminUserCards] = useState(null); // { email, cards, loading }

  const [vipList, setVipList] = useState(null);
  const [vipEmailInput, setVipEmailInput] = useState('');
  const [vipLimitInput, setVipLimitInput] = useState('250');
  const [vipSaving, setVipSaving] = useState(false);
  const [vipError, setVipError] = useState(null);
  const [showOnboarding, setShowOnboarding] = useState(false);
  const [marketingConsent, setMarketingConsent] = useState(undefined);
  const [exportHistory, setExportHistory] = useState([]);
  // Front URLs already counted toward the usage limit this session — re-approving
  // after a re-scan must not increment the count again
  const countedRef = useRef(new Set());

  const saveMarketingConsent = async (consent) => {
    setMarketingConsent(consent);
    try {
      const token = await getToken();
      fetch(`${API_BASE}/api/user/marketing-consent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ consent }),
      });
    } catch {}
  };

  const handleOnboardingConfirm = async (consent) => {
    setShowOnboarding(false);
    saveMarketingConsent(consent);
  };

  const handleSettingsChange = async (newSettings) => {
    setSettings(newSettings);
    saveSettings(newSettings);
    try {
      const token = await getToken();
      fetch(`${API_BASE}/api/user/settings`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(newSettings),
      });
    } catch {}
  };

  const { isSignedIn, userId, getToken } = useAuth();
  const isMobile = useIsMobile();
  const { signOut, user } = useClerk();
  const [userUsage, setUserUsage] = useState(null);

  const saveSession = async (sessionUrls, sessionCards, currentResult = null) => {
    try {
      const token = await getToken();
      const body = { urls: sessionUrls, approvedCards: sessionCards };
      if (currentResult) body.currentResult = currentResult;
      fetch(`${API_BASE}/api/user/session`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });
    } catch {}
  };

  const handleDetect = (cardIndex, data) => {
    saveSession(urls, approvedCards, { index: cardIndex, data, edits: null });
  };

  const handleEditsChange = (cardIndex, editsForCard, detectionData, quantity = null) => {
    const currentResult = { index: cardIndex, data: detectionData, edits: editsForCard };
    if (quantity !== null) currentResult.quantity = quantity;
    saveSession(urls, approvedCards, currentResult);
  };

  const clearSession = async () => {
    try {
      const token = await getToken();
      fetch(`${API_BASE}/api/user/session`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
    } catch {}
  };

  const loadVips = async () => {
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/api/admin/vips`, { headers: { 'Authorization': `Bearer ${token}` } });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed to load VIP list');
      setVipList(data.vips);
    } catch (e) {
      setVipError(e.message);
    }
  };

  const addVip = async () => {
    const email = vipEmailInput.trim();
    const cardLimit = parseInt(vipLimitInput, 10);
    if (!email || !email.includes('@')) return setVipError('Enter a valid email');
    if (!Number.isFinite(cardLimit) || cardLimit <= 0) return setVipError('Enter a valid card limit');
    setVipSaving(true);
    setVipError(null);
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/api/admin/vips`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ email, cardLimit }),
      });
      const data = await r.json();
      if (!r.ok) throw new Error(data.error || 'Failed to add VIP');
      setVipEmailInput('');
      await loadVips();
    } catch (e) {
      setVipError(e.message);
    }
    setVipSaving(false);
  };

  const removeVip = async (id) => {
    setVipSaving(true);
    try {
      const token = await getToken();
      const r = await fetch(`${API_BASE}/api/admin/vips/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!r.ok) throw new Error('Failed to remove VIP');
      await loadVips();
    } catch (e) {
      setVipError(e.message);
    }
    setVipSaving(false);
  };

  useEffect(() => {
    if (!isSignedIn || !userId) return;
    setExportHistory(loadExportHistory(userId));
    const email = user?.primaryEmailAddress?.emailAddress || '';
    fetch(`${API_BASE}/api/user/sync`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clerkId: userId, email })
    })
      .then(r => r.json())
      .then(data => {
        setUserUsage(data);
        setMarketingConsent(data.marketingConsent);
        if (data.marketingConsent === null) setShowOnboarding(true);
      })
      .catch(err => console.error('User sync failed:', err));

    // Load settings and session from server
    getToken().then(async token => {
      // Settings
      fetch(`${API_BASE}/api/user/settings`, { headers: { 'Authorization': `Bearer ${token}` } })
        .then(r => r.ok ? r.json() : null)
        .then(serverSettings => {
          if (serverSettings && Object.keys(serverSettings).length > 0) {
            const merged = { ...DEFAULT_SETTINGS, ...serverSettings };
            setSettings(merged);
            saveSettings(merged);
          }
        })
        .catch(() => {});

      // Session
      try {
        const r = await fetch(`${API_BASE}/api/user/session`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (r.ok) {
          const data = await r.json();
          if (data && data.urls && data.urls.length > 0) {
            setPendingSession(data);
          }
        }
      } catch {}
    });
  }, [isSignedIn, userId]); // eslint-disable-line react-hooks/exhaustive-deps
  const toggle = () => setTheme(t => t === 'light' ? 'dark' : 'light');

  const border = theme === 'dark' ? '#2e2c28' : '#dddad4';
  const bg = theme === 'dark' ? '#141412' : '#f7f6f3';
  const text = theme === 'dark' ? '#f0ede8' : '#1a1916';
  const text2 = theme === 'dark' ? '#857f76' : '#6b6760';
  const green = '#4a9b6f';

  const handleStart = (lines) => {
    setUrls(lines);
    setShowModal(false);
    setStarted(true);
    setApprovedCards([]);
    setPendingSession(null);
    setResumedCards([]);
    setResumedCurrentResult(null);
    saveSession(lines, []);
  };

  const handleNewSession = () => {
    setUrls([]);
    setStarted(false);
    setApprovedCards([]);
    setPendingSession(null);
    setResumedCards([]);
    setResumedCurrentResult(null);
    saveSession([], []);
  };

  const handleAddMore = (lines) => {
    setUrls(prev => {
      const next = [...prev, ...lines];
      saveSession(next, approvedCards);
      return next;
    });
    setShowAddModal(false);
  };

  const buildExportBody = (customSku, scheduleDate, cardsPerDay, cards = approvedCards) => ({
    cards,
    customSku,
    scheduleDate,
    cardsPerDay: cardsPerDay ? parseInt(cardsPerDay) : null,
    // eBay reads Schedule Time as GMT, so the server needs to know which zone
    // the wall-clock time in scheduleDate was typed in.
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || '',
    market: settings.ebayMarket || 'AU',
    shippingProfile: settings.shippingProfile,
    returnProfile: settings.returnProfile,
    paymentProfile: settings.paymentProfile,
    location: settings.location,
    storeCategoryId: settings.storeCategoryId,
    descriptionMode: settings.descriptionMode || 'auto',
    customDescription: settings.customDescription || '',
    hideBranding: !!settings.hideBranding,
  });

  // POST the export body and trigger a browser download of the returned CSV.
  // The token lets the server verify the plan when hideBranding is requested.
  const downloadCsv = async (body) => {
    const headers = { 'Content-Type': 'application/json' };
    try {
      const token = await getToken();
      if (token) headers['Authorization'] = `Bearer ${token}`;
    } catch {}
    const response = await fetch(`${API_BASE}/api/export`, {
      method: 'POST',
      headers,
      body: JSON.stringify(body),
    });
    if (!response.ok) throw new Error('Export failed');
    const blob = await response.blob();
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'bodega_export.csv';
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const rememberExport = (body, type) => {
    if (!userId) return;
    // Strip the bulky scp match data — buildCSV never reads it — and count
    // the way the server does (it dedups by front|back URL before writing rows)
    const slimCards = body.cards.map(({ scp, ...rest }) => rest);
    const count = new Set(slimCards.map(c => [c.frontUrl, c.backUrl].join('|'))).size;
    setExportHistory(recordExport(userId, {
      date: new Date().toISOString(),
      count,
      sku: body.customSku || '',
      type,
      body: { ...body, cards: slimCards },
    }));
  };

  const handleExportConfirm = async (customSku, scheduleDate, cardsPerDay) => {
    setShowExportModal(false);
    setLimitReached(false);
    try {
      const body = buildExportBody(customSku, scheduleDate, cardsPerDay);
      await downloadCsv(body);
      rememberExport(body, 'download');
      setExportedCount(approvedCards.length);
      saveSession([], []);
    } catch (err) {
      alert('Export failed. Make sure the server is running.');
    }
  };

  const handleRedownload = async (entry) => {
    try {
      await downloadCsv(entry.body);
    } catch {
      setErrorBanner('Re-download failed. Check your connection and try again.');
    }
  };

  const handleEmailExport = async (customSku, scheduleDate, cardsPerDay) => {
    try {
      const token = await getToken();
      const body = buildExportBody(customSku, scheduleDate, cardsPerDay);
      const res = await fetch(`${API_BASE}/api/export/email`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(body),
      });
      const data = await res.json();
      if (data.ok) rememberExport(body, 'email');
      return !!data.ok;
    } catch {
      return false;
    }
  };

  const handleSelectPlan = async (plan) => {
    const email = user?.primaryEmailAddress?.emailAddress || '';
    const res = await fetch(`${API_BASE}/api/stripe/checkout`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clerkId: userId, email, plan }),
    });
    const data = await res.json();
    if (data.url) window.location.href = data.url;
  };

  const handleManageBilling = async () => {
    const res = await fetch(`${API_BASE}/api/stripe/portal`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ clerkId: userId }),
    });
    const data = await res.json();
    if (data.url) window.location.href = data.url;
  };

  const doApprove = async (card, currentIndex, total) => {
    if (!countedRef.current.has(card.frontUrl)) {
      try {
        const res = await fetch(`${API_BASE}/api/user/approve`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ clerkId: userId, card: { ...card, market: settings.ebayMarket || 'AU' } })
        });
        const data = await res.json();
        if (res.status === 403) {
          setUserUsage(data);
          setLimitReached(true);
          setShowExportModal(true);
          return;
        }
        setUserUsage(data);
        countedRef.current.add(card.frontUrl);
      } catch (err) {
        setErrorBanner('Failed to approve card. Check your connection and try again.');
      }
    }
    setApprovedCards(prev => {
      const next = prev.filter(Boolean);
      const pos = next.findIndex(c => c.frontUrl && c.frontUrl === card.frontUrl);
      if (pos !== -1) {
        // Keep a title set in the bulk editor unless review supplied its own
        const titleOverride = card.titleOverride != null ? card.titleOverride : (next[pos].titleOverride ?? null);
        next[pos] = { ...card, titleOverride };
      } else {
        next.push(card);
      }
      saveSession(urls, next);
      return next;
    });
    if (currentIndex + 1 === total) setShowExportModal(true);
  };

  return (
    <div style={{ minHeight: '100vh', background: bg, color: text, transition: 'all 0.2s' }}>
      {!isSignedIn && (
        <div style={{ minHeight: '100vh', background: bg, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: '40px 20px', gap: 40 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12, textAlign: 'center' }}>
            <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 32, fontWeight: 700, color: text }}>Bodega.</div>
            <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 13, color: text2, letterSpacing: '-0.02em' }}>photograph. identify. list.</div>
            <div style={{ fontSize: 14, color: text2, maxWidth: 380, lineHeight: 1.7, marginTop: 4, fontWeight: 300 }}>
              Sports card identification and bulk eBay listing. Photograph your cards, every field read automatically, export a CSV — done.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 10, marginTop: 8, maxWidth: 420, width: '100%' }}>
              {[['01', 'Drop card images'], ['02', 'Identifies every field'], ['03', 'Export bulk eBay CSV']].map(([num, label]) => (
                <div key={num} style={{ padding: 12, background: theme === 'dark' ? '#1c1b18' : '#eeecea', borderRadius: 8, border: `1px solid ${border}` }}>
                  <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: text2, marginBottom: 4 }}>{num}</div>
                  <div style={{ fontSize: 12, color: text, lineHeight: 1.4 }}>{label}</div>
                </div>
              ))}
            </div>
            <div style={{ fontSize: 11, color: text2, fontFamily: 'IBM Plex Mono, monospace', marginTop: 4 }}>
              Free to start · 20 cards included · No credit card required
            </div>
            <button
              onClick={() => setShowLandingPricing(true)}
              style={{ fontSize: 12, color: '#fff', fontFamily: 'IBM Plex Mono, monospace', background: green, border: 'none', cursor: 'pointer', padding: '6px 14px', borderRadius: 5, fontWeight: 500 }}>
              View pricing →
            </button>
          </div>
          <SignIn routing="hash" />
          <div style={{ fontSize: 11, color: text2, fontFamily: 'IBM Plex Mono, monospace' }}>
            <a href="/privacy.html" style={{ color: text2 }}>Privacy Policy</a>
          </div>
        </div>
      )}

      {/* LANDING PRICING MODAL */}
      {showLandingPricing && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100, padding: 24 }}>
          <div style={{ background: theme === 'dark' ? '#1c1b18' : '#ffffff', border: `1px solid ${border}`, borderRadius: 12, padding: 32, width: '100%', maxWidth: 760, display: 'flex', flexDirection: 'column', gap: 24 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
              <div>
                <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 14, fontWeight: 600, color: text }}>Pricing</div>
                <div style={{ fontSize: 12, color: text2, marginTop: 4 }}>Billed monthly. Cancel anytime.</div>
              </div>
              <button onClick={() => setShowLandingPricing(false)} style={{ background: 'transparent', border: 'none', color: text2, fontSize: 18, cursor: 'pointer', lineHeight: 1 }}>×</button>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16 }}>
              {[
                { name: 'Free', price: '$0', limit: '20 cards/month', features: ['20 cards per month', 'eBay CSV export', 'Card detection', 'Session resume'] },
                { name: 'Concord', price: '$9.95', limit: 'Unlimited cards', features: ['Unlimited cards per month', 'eBay CSV export', 'Card detection', 'Session resume', 'Detection rules', 'Listing templates', 'Remove Bodega. branding'] },
              ].map(plan => (
                <div key={plan.name} style={{ border: `1px solid ${border}`, borderRadius: 8, padding: 20, display: 'flex', flexDirection: 'column', gap: 12, background: bg }}>
                  <div>
                    <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, fontWeight: 600, color: text }}>{plan.name}</div>
                    <div style={{ fontSize: 22, fontWeight: 700, color: text, marginTop: 6 }}>{plan.price}<span style={{ fontSize: 12, fontWeight: 400, color: text2 }}>/mo</span></div>
                    <div style={{ fontSize: 11, color: green, marginTop: 2, fontFamily: 'IBM Plex Mono, monospace' }}>{plan.limit}</div>
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, flex: 1 }}>
                    {plan.features.map(f => (
                      <div key={f} style={{ fontSize: 11, color: text2, display: 'flex', gap: 6, alignItems: 'center' }}>
                        <span style={{ color: green }}>✓</span> {f}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
            <div style={{ borderTop: `1px solid ${border}`, paddingTop: 16, textAlign: 'center' }}>
              <div style={{ fontSize: 12, color: text2 }}>Sign up for free to get started. Upgrade anytime from within the app.</div>
            </div>
          </div>
        </div>
      )}
      {isSignedIn && (<>

      {/* NAV */}
      <nav style={{ minHeight: 48, borderBottom: `1px solid ${border}`, display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0 16px', position: 'sticky', top: 0, background: bg, zIndex: 10, flexWrap: 'wrap', gap: 8 }}>
        <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 18, fontWeight: 700, cursor: 'pointer' }} onClick={() => started ? setShowNewBoardConfirm(true) : null}>Bodega.</div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {started && (
            <>
              {!isMobile && (
                <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: approvedCards.length > 0 ? green : text2 }}>
                  {approvedCards.length} approved
                </div>
              )}
              {userUsage && !isMobile && (
                <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: userUsage.remaining <= 5 ? '#c47c2b' : text2, borderLeft: `1px solid ${border}`, paddingLeft: 8 }}>
                  {userUsage.cardsUsed}/{userUsage.limit}
                </div>
              )}
              {!isMobile && (
                <button
                  onClick={() => setShowAddModal(true)}
                  style={{ height: 30, padding: '0 12px', borderRadius: 5, border: `1px solid ${border}`, background: 'transparent', fontSize: 12, color: text2, cursor: 'pointer' }}>
                  + Add
                </button>
              )}
              <button
                onClick={() => {
                  if (approvedCards.length === 0) {
                    alert('No approved cards to export yet. Approve some cards first!');
                    return;
                  }
                  setShowExportModal(true);
                }}
                style={{ height: 30, padding: '0 12px', borderRadius: 5, border: `1px solid ${border}`, background: 'transparent', fontSize: 12, color: approvedCards.length > 0 ? green : text2, cursor: 'pointer' }}>
                ↓ Export
              </button>
            </>
          )}
          <button onClick={() => started ? setShowNewBoardConfirm(true) : setShowModal(true)} style={{ height: 30, padding: '0 12px', borderRadius: 5, border: 'none', background: text, color: bg, fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
            {isMobile ? '+ New' : 'New board'}
          </button>
          {isSignedIn && (!userUsage || userUsage.plan === 'free') && !isMobile && (
            <button onClick={() => setShowPricing(true)} style={{ height: 30, padding: '0 12px', borderRadius: 5, border: 'none', background: green, color: '#fff', fontSize: 12, fontWeight: 500, cursor: 'pointer' }}>
              Upgrade to Concord →
            </button>
          )}
          {RESELLER_USER_IDS.includes(userId) && (
            <button
              onClick={() => { setShowReseller(r => !r); setShowAdmin(false); }}
              style={{ height: 30, padding: '0 12px', borderRadius: 5, border: `1px solid ${showReseller ? text : border}`, background: showReseller ? text : (theme === 'dark' ? '#1c1b18' : '#eeecea'), cursor: 'pointer', fontSize: 11, fontFamily: 'IBM Plex Mono, monospace', color: showReseller ? bg : text2 }}>
              Reseller
            </button>
          )}
          {['user_3BCjA4weoBnajSAikWSX7UkvlC6', 'user_3CFLjclKTHYzmdho62av96S6GPs'].includes(userId) && (
            <button onClick={async () => {
              if (showAdmin) { setShowAdmin(false); return; }
              setShowAdmin(true); setShowReseller(false);
              if (!vipList) loadVips();
              if (!adminStats) {
                setAdminLoading(true);
                try {
                  const token = await getToken();
                  const r = await fetch(`${API_BASE}/api/admin/stats`, { headers: { 'Authorization': `Bearer ${token}` } });
                  const data = await r.json();
                  if (!r.ok) throw new Error(`${r.status}: ${data.error || 'unknown'}`);
                  setAdminStats(data);
                } catch (e) {
                  setAdminStats({ error: e.message });
                }
                setAdminLoading(false);
              }
            }} style={{ width: 30, height: 30, borderRadius: 5, border: `1px solid ${showAdmin ? text : border}`, background: showAdmin ? text : (theme === 'dark' ? '#1c1b18' : '#eeecea'), cursor: 'pointer', fontSize: 12, fontFamily: 'IBM Plex Mono, monospace', color: showAdmin ? bg : text2 }}>◈</button>
          )}
          <button onClick={() => setShowFeedback(true)} title="Feedback" style={{ width: 30, height: 30, borderRadius: 5, border: `1px solid ${border}`, background: theme === 'dark' ? '#1c1b18' : '#eeecea', cursor: 'pointer', fontSize: 14 }}>✎</button>
          <button onClick={() => setShowSettings(true)} style={{ width: 30, height: 30, borderRadius: 5, border: `1px solid ${border}`, background: theme === 'dark' ? '#1c1b18' : '#eeecea', cursor: 'pointer', fontSize: 14 }}>⚙</button>
          <button onClick={toggle} style={{ width: 30, height: 30, borderRadius: 5, border: `1px solid ${border}`, background: theme === 'dark' ? '#1c1b18' : '#eeecea', cursor: 'pointer', fontSize: 13 }}>
            {theme === 'dark' ? '☽' : '☀'}
          </button>
          {!isMobile && (
            <UserButton afterSignOutUrl="/" />
          )}
        </div>
      </nav>

      {/* RESELLER */}
      {showReseller && <Reseller theme={theme} getToken={getToken} />}

      {/* MAIN */}
      {!showAdmin && !showReseller && started ? (
        <ReviewLayout
          theme={theme}
          market={settings.ebayMarket || 'AU'}
          urls={urls}
          resumedCards={resumedCards}
          initialResult={resumedCurrentResult}
          detectionRules={settings.detectionRules || []}
          templates={settings.listingTemplates || []}
          approvedCards={approvedCards}
          onDuplicateSkip={(originalFrontUrl, addQty, currentIndex, total, imagesToMerge) => {
            // Duplicate skipped in review — bump the original's quantity instead,
            // optionally also merging the duplicate scan's photos onto it (see
            // mergeCardImages in titleUtils.js)
            setApprovedCards(prev => {
              const next = prev.map(c => {
                if (!c || c.frontUrl !== originalFrontUrl) return c;
                const updated = { ...c, quantity: (c.quantity || 1) + (addQty || 1) };
                if (imagesToMerge && imagesToMerge.length) updated.extraImages = mergeCardImages(c, imagesToMerge);
                return updated;
              });
              saveSession(urls, next);
              return next;
            });
            if (currentIndex + 1 === total) setShowExportModal(true);
          }}
          keysDisabled={!!(showExportModal || showSettings || showFeedback || showModal || showAddModal || showNewBoardConfirm || showPricing)}
          onDetect={handleDetect}
          onEditsChange={handleEditsChange}
          onRescan={(cardIndex, card) => {
            // Drop any previously approved version of this card so stale data
            // can't reach the export; it comes back when the user re-approves
            setApprovedCards(prev => {
              const next = prev.filter(c => c && c.frontUrl !== card.front);
              if (next.length !== prev.length) saveSession(urls, next);
              return next;
            });
          }}
          onApprove={doApprove}
          onApproveAndExport={(card) => {
            // Upsert by frontUrl, matching doApprove — currentIndex is the
            // card's position in this batch, not its position in
            // approvedCards (which is ordered by approval order), so using
            // it as an array index here overwrote the wrong slot whenever
            // any earlier card had been skipped.
            setApprovedCards(prev => {
              const next = prev.filter(Boolean);
              const pos = next.findIndex(c => c.frontUrl && c.frontUrl === card.frontUrl);
              if (pos !== -1) next[pos] = card;
              else next.push(card);
              saveSession(urls, next);
              return next;
            });
            setShowExportModal(true);
          }}
        />
      ) : !showAdmin && !showReseller ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', minHeight: 'calc(100vh - 48px)', gap: 16, textAlign: 'center', padding: 40 }}>
          {pendingSession ? (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 20, maxWidth: 380 }}>
              <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 13, color: text2, letterSpacing: '-0.02em' }}>Unfinished session</div>
              <div style={{ background: theme === 'dark' ? '#1c1b18' : '#ffffff', border: `1px solid ${border}`, borderRadius: 10, padding: 24, width: '100%', display: 'flex', flexDirection: 'column', gap: 16 }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: text2 }}>Cards in batch</span>
                  <span style={{ fontFamily: 'IBM Plex Mono, monospace', color: text }}>{Math.floor(pendingSession.urls.length / 2)}</span>
                </div>
                <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 13 }}>
                  <span style={{ color: text2 }}>Approved</span>
                  <span style={{ fontFamily: 'IBM Plex Mono, monospace', color: green }}>{pendingSession.approvedCards.length}</span>
                </div>
                <div style={{ display: 'flex', gap: 10, marginTop: 4 }}>
                  <button
                    onClick={() => {
                      setUrls(pendingSession.urls);
                      setApprovedCards(pendingSession.approvedCards);
                      pendingSession.approvedCards.forEach(c => { if (c?.frontUrl) countedRef.current.add(c.frontUrl); });
                      setResumedCards(pendingSession.approvedCards);
                      setResumedCurrentResult(pendingSession.currentResult || null);
                      setStarted(true);
                      setPendingSession(null);
                    }}
                    style={{ flex: 1, height: 38, borderRadius: 6, border: 'none', background: text, color: bg, fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                    Resume session
                  </button>
                  <button
                    onClick={() => { setPendingSession(null); saveSession([], []); }}
                    style={{ flex: 1, height: 38, borderRadius: 6, border: `1px solid ${border}`, background: 'transparent', color: text2, fontSize: 13, cursor: 'pointer' }}>
                    Start fresh
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <>
              <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 22, fontWeight: 500, letterSpacing: '-0.03em' }}>drop. process. list.</div>
              <div style={{ fontSize: 14, color: text2, maxWidth: 360, lineHeight: 1.6, fontWeight: 300 }}>
                Automated card detection and bulk eBay CSV generation for sports card sellers.
              </div>
              <button onClick={() => setShowModal(true)} style={{ marginTop: 8, padding: '12px 28px', background: text, color: bg, border: 'none', borderRadius: 6, fontSize: 14, fontWeight: 500, cursor: 'pointer' }}>
                Start a new session →
              </button>
              <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 10, color: '#a8a49e', letterSpacing: '0.05em' }}>
                Drop card images to begin
              </div>
              {(!userUsage || userUsage.plan === 'free') && (
                <button onClick={() => setShowPricing(true)} style={{ marginTop: 4, padding: '8px 20px', background: 'transparent', color: green, border: `1px solid ${green}`, borderRadius: 6, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'IBM Plex Mono, monospace' }}>
                  Upgrade to Concord — unlimited cards for $9.95/mo
                </button>
              )}
            </>
          )}
        </div>
      ) : null}

      {/* URL MODAL */}
      {showModal && <Modal theme={theme} onStart={handleStart} onClose={() => setShowModal(false)} />}


      {showNewBoardConfirm && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
          <div style={{ background: theme === 'dark' ? '#1c1b18' : '#ffffff', border: `1px solid ${border}`, borderRadius: 10, padding: 28, width: 340, display: 'flex', flexDirection: 'column', gap: 20 }}>
            <div>
              <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 13, fontWeight: 600, color: theme === 'dark' ? '#f0ede8' : '#1a1916' }}>Start a new board?</div>
              <div style={{ fontSize: 12, color: theme === 'dark' ? '#857f76' : '#6b6760', marginTop: 6, lineHeight: 1.6 }}>
                This will clear your current session including {approvedCards.length} approved card{approvedCards.length !== 1 ? 's' : ''}. This can't be undone.
              </div>
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowNewBoardConfirm(false)}
                style={{ height: 34, padding: '0 16px', borderRadius: 6, border: `1px solid ${border}`, background: 'transparent', color: theme === 'dark' ? '#857f76' : '#6b6760', fontSize: 13, cursor: 'pointer' }}>
                Cancel
              </button>
              <button
                onClick={() => { setShowNewBoardConfirm(false); handleNewSession(); setShowModal(true); }}
                style={{ height: 34, padding: '0 16px', borderRadius: 6, border: 'none', background: theme === 'dark' ? '#f0ede8' : '#1a1916', color: theme === 'dark' ? '#141412' : '#ffffff', fontSize: 13, fontWeight: 500, cursor: 'pointer' }}>
                Yes, start fresh
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ADD CARDS MODAL */}
      {showAddModal && <Modal theme={theme} onStart={handleAddMore} onClose={() => setShowAddModal(false)} />}

      {/* ADMIN PAGE */}
      {showAdmin && (() => {
        const StatBox = ({ label, value }) => (
          <div style={{ flex: 1, background: bg, border: `1px solid ${border}`, borderRadius: 8, padding: '16px 20px' }}>
            <div style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 24, fontWeight: 600, color: text }}>{value ?? '—'}</div>
            <div style={{ fontSize: 11, color: text2, marginTop: 6, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</div>
          </div>
        );
        const Section = ({ title, children }) => (
          <div>
            <div style={{ fontSize: 10, color: text2, marginBottom: 12, textTransform: 'uppercase', letterSpacing: 1.5, fontFamily: 'IBM Plex Mono, monospace' }}>{title}</div>
            {children}
          </div>
        );
        const Table = ({ rows, col1, col2 }) => (
          <div style={{ border: `1px solid ${border}`, borderRadius: 8, overflow: 'hidden' }}>
            {rows.length === 0
              ? <div style={{ padding: '12px 16px', fontSize: 12, color: text2 }}>No data yet</div>
              : rows.map((r, i) => (
                <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: i < rows.length - 1 ? `1px solid ${border}` : 'none', background: i % 2 === 0 ? bg : 'transparent' }}>
                  <span style={{ fontSize: 13, color: text }}>{r[col1] || '—'}</span>
                  <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 12, color: text2 }}>{r[col2]}</span>
                </div>
              ))
            }
          </div>
        );
        return (
          <div style={{ padding: '32px 40px', maxWidth: 900, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 36 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
              <div>
                <div style={{ fontFamily: 'Playfair Display, serif', fontSize: 22, fontWeight: 700, color: text }}>Dashboard</div>
                <div style={{ fontSize: 12, color: text2, marginTop: 4 }}>Bodega. platform overview</div>
              </div>
              <button onClick={async () => {
                setAdminStats(null);
                setAdminLoading(true);
                loadVips();
                try {
                  const token = await getToken();
                  const r = await fetch(`${API_BASE}/api/admin/stats`, { headers: { 'Authorization': `Bearer ${token}` } });
                  const data = await r.json();
                  if (!r.ok) throw new Error(`${r.status}: ${data.error || 'unknown'}`);
                  setAdminStats(data);
                } catch (e) {
                  setAdminStats({ error: e.message });
                }
                setAdminLoading(false);
              }} style={{ height: 30, padding: '0 14px', borderRadius: 5, border: `1px solid ${border}`, background: 'transparent', fontSize: 12, color: text2, cursor: 'pointer', fontFamily: 'IBM Plex Mono, monospace' }}>↺ Refresh</button>
            </div>

            <Section title="VIPs & promos">
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                <div style={{ fontSize: 12, color: text2 }}>
                  Add an email to grant that card limit immediately, regardless of their plan — no questions asked. Works even before they've signed up. Remove the email to revert them to their normal plan limit.
                </div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <input
                    type="email"
                    placeholder="email@example.com"
                    value={vipEmailInput}
                    onChange={e => setVipEmailInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addVip()}
                    style={{ flex: '1 1 240px', height: 34, padding: '0 12px', borderRadius: 5, border: `1px solid ${border}`, background: bg, color: text, fontSize: 13 }}
                  />
                  <input
                    type="number"
                    min="1"
                    placeholder="250"
                    value={vipLimitInput}
                    onChange={e => setVipLimitInput(e.target.value)}
                    onKeyDown={e => e.key === 'Enter' && addVip()}
                    style={{ width: 100, height: 34, padding: '0 12px', borderRadius: 5, border: `1px solid ${border}`, background: bg, color: text, fontSize: 13, fontFamily: 'IBM Plex Mono, monospace' }}
                  />
                  <button onClick={addVip} disabled={vipSaving} style={{ height: 34, padding: '0 16px', borderRadius: 5, border: 'none', background: text, color: bg, fontSize: 12, fontWeight: 500, cursor: vipSaving ? 'default' : 'pointer', opacity: vipSaving ? 0.6 : 1 }}>
                    + Add
                  </button>
                </div>
                {vipError && <div style={{ fontSize: 12, color: '#e74c3c' }}>{vipError}</div>}
                <div style={{ border: `1px solid ${border}`, borderRadius: 8, overflow: 'hidden' }}>
                  {!vipList
                    ? <div style={{ padding: '12px 16px', fontSize: 12, color: text2 }}>Loading...</div>
                    : vipList.length === 0
                      ? <div style={{ padding: '12px 16px', fontSize: 12, color: text2 }}>No VIPs yet</div>
                      : vipList.map((v, i) => (
                        <div key={v.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 16px', borderBottom: i < vipList.length - 1 ? `1px solid ${border}` : 'none', background: i % 2 === 0 ? bg : 'transparent' }}>
                          <div>
                            <div style={{ fontSize: 13, color: text }}>{v.email}</div>
                            <div style={{ fontSize: 11, color: text2, marginTop: 2 }}>
                              {v.current_plan ? `${v.current_plan} plan · ${v.cards_used} used` : 'not signed up yet'}
                            </div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                            <span style={{ fontFamily: 'IBM Plex Mono, monospace', fontSize: 13, color: green }}>{v.card_limit} cards</span>
                            <button onClick={() => removeVip(v.id)} disabled={vipSaving} style={{ height: 26, padding: '0 10px', borderRadius: 5, border: `1px solid ${border}`, background: 'transparent', fontSize: 11, color: text2, cursor: vipSaving ? 'default' : 'pointer' }}>
                              Remove
                            </button>
                          </div>
                        </div>
                      ))
                  }
                </div>
              </div>
            </Section>

            {adminLoading && <div style={{ fontSize: 13, color: text2 }}>Loading...</div>}
            {adminStats?.error && <div style={{ fontSize: 13, color: '#e74c3c', fontFamily: 'IBM Plex Mono, monospace' }}>Error: {adminStats.error}</div>}

            {adminStats && !adminStats.error && (<>
              <Section title="Users">
                <div style={{ display: 'flex', gap: 12 }}>
                  <StatBox label="Total" value={adminStats.users.total} />
                  <StatBox label="New this week" value={adminStats.users.newThisWeek} />
                  <StatBox label="New this month" value={adminStats.users.newThisMonth} />
                </div>
              </Section>

              <Section title="Cards approved">
                <div style={{ display: 'flex', gap: 12 }}>
                  <StatBox label="All time" value={adminStats.cards.total} />
                  <StatBox label="This week" value={adminStats.cards.thisWeek} />
                  <StatBox label="This month" value={adminStats.cards.thisMonth} />
                </div>
              </Section>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
                <Section title="Plan breakdown">
                  <Table rows={adminStats.users.plans} col1="plan" col2="count" />
                </Section>
                <Section title="By sport">
                  <Table rows={adminStats.cards.bySport} col1="sport" col2="count" />
                </Section>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
                <Section title="Top players">
                  <Table rows={adminStats.cards.topPlayers} col1="player" col2="count" />
                </Section>
                <Section title="Top sets">
                  <Table rows={adminStats.cards.topSets} col1="set_name" col2="count" />
                </Section>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 32 }}>
                <Section title="Top years">
                  <Table rows={adminStats.cards.topYears || []} col1="year" col2="count" />
                </Section>
                <Section title="Peak hours (UTC)">
                  <Table rows={(adminStats.cards.byHour || []).map(r => ({ ...r, hour: `${String(r.hour).padStart(2, '0')}:00` }))} col1="hour" col2="count" />
                </Section>
              </div>

              <Section title="Activity by day of week">
                <Table rows={(adminStats.cards.byDayOfWeek || []).map(r => ({ ...r, day_of_week: r.day_of_week?.trim() }))} col1="day_of_week" col2="count" />
              </Section>

              <Section title="Cards per day — last 30 days">
                <Table rows={adminStats.cards.perDay.map(r => ({ ...r, day: r.day?.slice(0, 10) }))} col1="day" col2="count" />
              </Section>

              <Section title="All users">
                <div style={{ overflowX: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12, fontFamily: 'IBM Plex Mono, monospace' }}>
                    <thead>
                      <tr>
                        {['Email', 'Plan', 'Cards used', 'Marketing', 'Signed up', ''].map(h => (
                          <th key={h} style={{ textAlign: 'left', padding: '6px 12px', borderBottom: `1px solid ${border}`, color: text2, fontWeight: 400, letterSpacing: '0.04em', fontSize: 10, textTransform: 'uppercase' }}>{h}</th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {(adminStats.users.list || []).map((u, i) => (
                        <tr key={i} style={{ borderBottom: `1px solid ${border}`, background: adminUserCards?.email === u.email ? (theme === 'dark' ? 'rgba(74,155,111,0.08)' : 'rgba(74,155,111,0.05)') : 'transparent' }}>
                          <td style={{ padding: '8px 12px', color: text }}>{u.email || '—'}</td>
                          <td style={{ padding: '8px 12px', color: text2 }}>{u.plan}</td>
                          <td style={{ padding: '8px 12px', color: text2 }}>{u.cards_used}</td>
                          <td style={{ padding: '8px 12px', color: u.marketing_emails ? green : text2 }}>{u.marketing_emails === null ? '—' : u.marketing_emails ? 'Yes' : 'No'}</td>
                          <td style={{ padding: '8px 12px', color: text2 }}>{u.created_at ? new Date(u.created_at).toLocaleDateString() : '—'}</td>
                          <td style={{ padding: '8px 12px' }}>
                            <button
                              onClick={async () => {
                                if (adminUserCards?.email === u.email) { setAdminUserCards(null); return; }
                                setAdminUserCards({ email: u.email, cards: null, loading: true });
                                try {
                                  const token = await getToken();
                                  const r = await fetch(`${API_BASE}/api/admin/user-cards?email=${encodeURIComponent(u.email)}`, { headers: { 'Authorization': `Bearer ${token}` } });
                                  const data = await r.json();
                                  setAdminUserCards({ email: u.email, cards: data.cards || [], loading: false });
                                } catch {
                                  setAdminUserCards({ email: u.email, cards: [], loading: false, error: true });
                                }
                              }}
                              style={{ height: 24, padding: '0 10px', borderRadius: 4, border: `1px solid ${border}`, background: 'transparent', fontSize: 11, color: text2, cursor: 'pointer' }}>
                              {adminUserCards?.email === u.email ? 'Hide' : 'View cards'}
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </Section>

              {adminUserCards && (
                <Section title={`Cards — ${adminUserCards.email}`}>
                  {adminUserCards.loading
                    ? <div style={{ fontSize: 12, color: text2 }}>Loading...</div>
                    : adminUserCards.error
                      ? <div style={{ fontSize: 12, color: '#e74c3c' }}>Failed to load cards</div>
                      : adminUserCards.cards.length === 0
                        ? <div style={{ fontSize: 12, color: text2 }}>No cards approved yet</div>
                        : <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
                            {adminUserCards.cards.map((c, i) => (
                              <div key={i} style={{ border: `1px solid ${border}`, borderRadius: 8, padding: 16, display: 'flex', gap: 16, background: bg }}>
                                <div style={{ display: 'flex', gap: 8, flexShrink: 0 }}>
                                  {c.front_url && <img src={c.front_url} alt="front" style={{ width: 60, height: 84, objectFit: 'cover', borderRadius: 4, border: `1px solid ${border}` }} />}
                                  {c.back_url  && <img src={c.back_url}  alt="back"  style={{ width: 60, height: 84, objectFit: 'cover', borderRadius: 4, border: `1px solid ${border}` }} />}
                                </div>
                                <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 4 }}>
                                  <div style={{ fontSize: 13, fontWeight: 500, color: text }}>{c.ebay_title || '—'}</div>
                                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '4px 16px', marginTop: 4 }}>
                                    {[
                                      ['Player', c.player], ['Year', c.year], ['Set', c.set_name],
                                      ['Card #', c.card_number], ['Features', c.features], ['Sport', c.sport],
                                      ['League', c.league], ['Team', c.team], ['Condition', c.condition],
                                      ['Qty', c.quantity], ['Price', c.sell_price ? `$${c.sell_price}` : null],
                                      ['Market', c.market], ['SCP ID', c.scp_match_id],
                                    ].map(([label, val]) => val != null && val !== '' ? (
                                      <span key={label} style={{ fontSize: 11, color: text2, fontFamily: 'IBM Plex Mono, monospace' }}>
                                        <span style={{ color: text2, opacity: 0.6 }}>{label}: </span>{val}
                                      </span>
                                    ) : null)}
                                  </div>
                                  <div style={{ fontSize: 10, color: text2, marginTop: 4, fontFamily: 'IBM Plex Mono, monospace', opacity: 0.6 }}>
                                    {c.approved_at ? new Date(c.approved_at).toLocaleString() : ''}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                  }
                </Section>
              )}
            </>)}
          </div>
        );
      })()}

      {/* SETTINGS MODAL */}
      {showSettings && (
        <SettingsModal
          theme={theme}
          settings={settings}
          onChange={handleSettingsChange}
          onClose={() => setShowSettings(false)}
          marketingConsent={marketingConsent}
          onMarketingConsentChange={saveMarketingConsent}
          plan={userUsage?.plan || 'free'}
          exportHistory={exportHistory}
          onRedownload={handleRedownload}
          onDeleteExport={(id) => setExportHistory(deleteExportEntry(userId, id))}
        />
      )}

      {showFeedback && (
        <FeedbackModal
          theme={theme}
          getToken={getToken}
          onClose={() => setShowFeedback(false)}
        />
      )}

      {/* ERROR BANNER */}
      {errorBanner && (
        <div style={{ position: 'fixed', top: 56, left: '50%', transform: 'translateX(-50%)', background: '#b94040', color: '#fff', padding: '10px 20px', borderRadius: 6, fontSize: 12, fontFamily: 'IBM Plex Mono, monospace', zIndex: 200, display: 'flex', gap: 12, alignItems: 'center' }}>
          {errorBanner}
          <button onClick={() => setErrorBanner(null)} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: 16, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* POST-EXPORT CLEAR BANNER */}
      {exportedCount !== null && (
        <div style={{ position: 'fixed', top: 56, left: '50%', transform: 'translateX(-50%)', background: '#1c1b18', border: '1px solid #4a9b6f', color: '#f0ede8', padding: '10px 16px', borderRadius: 6, fontSize: 12, fontFamily: 'IBM Plex Mono, monospace', zIndex: 200, display: 'flex', gap: 12, alignItems: 'center', whiteSpace: 'nowrap' }}>
          {exportedCount} card{exportedCount !== 1 ? 's' : ''} exported.
          <button onClick={() => { setApprovedCards([]); setExportedCount(null); }} style={{ background: '#4a9b6f', border: 'none', color: '#fff', fontSize: 11, cursor: 'pointer', padding: '3px 10px', borderRadius: 4, fontFamily: 'IBM Plex Mono, monospace' }}>Clear queue</button>
          <button onClick={() => setExportedCount(null)} style={{ background: 'transparent', border: 'none', color: '#857f76', fontSize: 16, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* UPGRADE SUCCESS BANNER */}
      {upgradeSuccess && (
        <div style={{ position: 'fixed', top: 56, left: '50%', transform: 'translateX(-50%)', background: green, color: '#fff', padding: '10px 20px', borderRadius: 6, fontSize: 12, fontFamily: 'IBM Plex Mono, monospace', zIndex: 200, display: 'flex', gap: 12, alignItems: 'center' }}>
          Plan upgraded successfully!
          <button onClick={() => setUpgradeSuccess(false)} style={{ background: 'transparent', border: 'none', color: '#fff', fontSize: 16, cursor: 'pointer', lineHeight: 1 }}>×</button>
        </div>
      )}

      {/* ONBOARDING MODAL */}
      {showOnboarding && (
        <OnboardingModal theme={theme} onConfirm={handleOnboardingConfirm} />
      )}

      {/* PRICING MODAL */}
      {showPricing && (
        <PricingModal
          theme={theme}
          currentPlan={userUsage?.plan || 'free'}
          onClose={() => setShowPricing(false)}
          onSelectPlan={handleSelectPlan}
          onManageBilling={handleManageBilling}
          userId={userId}
        />
      )}

      {/* EXPORT MODAL */}
      {showExportModal && (
        <ExportModal
          theme={theme}
          approvedCards={approvedCards}
          limitReached={limitReached}
          userUsage={userUsage}
          settings={settings}
          onConfirm={handleExportConfirm}
          onEmailConfirm={handleEmailExport}
          userEmail={user?.primaryEmailAddress?.emailAddress || ''}
          onClose={() => { setShowExportModal(false); setLimitReached(false); }}
          onUpgrade={() => { setShowExportModal(false); setLimitReached(false); setShowPricing(true); }}
          onUpdateTitle={(i, title) => {
            setApprovedCards(prev => {
              const next = [...prev];
              if (!next[i]) return prev;
              next[i] = { ...next[i], titleOverride: title };
              saveSession(urls, next);
              return next;
            });
          }}
          onUpdateQuantity={(i, qty) => {
            setApprovedCards(prev => {
              const next = [...prev];
              if (!next[i]) return prev;
              const n = Math.max(1, Math.min(999, parseInt(qty) || 1));
              next[i] = { ...next[i], quantity: n };
              saveSession(urls, next);
              return next;
            });
          }}
        />
      )}

      </>)}
    </div>
  );
}

export default App;
